import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { profilesApi, studiesApi, priceListsApi } from "@/lib/api";
import { toast } from "sonner";
import { Layers, Plus, Pencil, Trash2, Search, X, ChevronUp, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function Profiles() {
  const [profiles, setProfiles] = useState([]);
  const [studies, setStudies] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    study_ids: [],
    price: 0,
    price_list_id: "",
    active: true,
  });
  
  // Selected studies with order
  const [selectedStudies, setSelectedStudies] = useState([]);
  
  // Study search
  const [studySearchTerm, setStudySearchTerm] = useState("");
  const [showStudyDropdown, setShowStudyDropdown] = useState(false);
  const [studyDropdownIndex, setStudyDropdownIndex] = useState(0);
  const studySearchRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (studySearchRef.current && !studySearchRef.current.contains(event.target)) {
        setShowStudyDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      const [profilesRes, studiesRes, priceListsRes] = await Promise.all([
        profilesApi.getAll(),
        studiesApi.getAll(),
        priceListsApi.getAll(),
      ]);
      setProfiles(profilesRes.data);
      setStudies(studiesRes.data);
      setPriceLists(priceListsRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (profile = null) => {
    if (profile) {
      setEditingProfile(profile);
      setFormData({
        name: profile.name,
        code: profile.code,
        description: profile.description || "",
        study_ids: profile.study_ids || [],
        price: profile.price,
        price_list_id: profile.price_list_id || "",
        active: profile.active,
      });
      // Load selected studies in order
      const studyList = (profile.study_ids || []).map(id => 
        studies.find(s => s.id === id)
      ).filter(Boolean);
      setSelectedStudies(studyList);
    } else {
      setEditingProfile(null);
      setFormData({
        name: "",
        code: "",
        description: "",
        study_ids: [],
        price: 0,
        price_list_id: "",
        active: true,
      });
      setSelectedStudies([]);
    }
    setStudySearchTerm("");
    setShowStudyDropdown(false);
    setDialogOpen(true);
  };

  // Filter studies for dropdown (exclude already selected)
  const filteredStudiesForDropdown = studies.filter(study => {
    if (!studySearchTerm) return false;
    const isSelected = selectedStudies.some(s => s.id === study.id);
    if (isSelected) return false;
    const searchLower = studySearchTerm.toLowerCase();
    return (
      study.name?.toLowerCase().includes(searchLower) ||
      study.code?.toLowerCase().includes(searchLower)
    );
  });

  // Add study to selection
  const addStudy = (study) => {
    if (!selectedStudies.some(s => s.id === study.id)) {
      setSelectedStudies(prev => [...prev, study]);
      setFormData(prev => ({
        ...prev,
        study_ids: [...prev.study_ids, study.id]
      }));
      toast.success(`${study.name} agregado`);
    }
    setStudySearchTerm("");
    setShowStudyDropdown(false);
    setStudyDropdownIndex(0);
  };

  // Remove study from selection
  const removeStudy = (studyId) => {
    setSelectedStudies(prev => prev.filter(s => s.id !== studyId));
    setFormData(prev => ({
      ...prev,
      study_ids: prev.study_ids.filter(id => id !== studyId)
    }));
  };

  // Move study up
  const moveStudyUp = (index) => {
    if (index === 0) return;
    setSelectedStudies(prev => {
      const newList = [...prev];
      [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
      return newList;
    });
    setFormData(prev => {
      const newIds = [...prev.study_ids];
      [newIds[index - 1], newIds[index]] = [newIds[index], newIds[index - 1]];
      return { ...prev, study_ids: newIds };
    });
  };

  // Move study down
  const moveStudyDown = (index) => {
    if (index === selectedStudies.length - 1) return;
    setSelectedStudies(prev => {
      const newList = [...prev];
      [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
      return newList;
    });
    setFormData(prev => {
      const newIds = [...prev.study_ids];
      [newIds[index], newIds[index + 1]] = [newIds[index + 1], newIds[index]];
      return { ...prev, study_ids: newIds };
    });
  };

  // Handle keyboard navigation in study search
  const handleStudySearchKeyDown = (e) => {
    if (!showStudyDropdown || filteredStudiesForDropdown.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setStudyDropdownIndex(prev => 
        prev < filteredStudiesForDropdown.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setStudyDropdownIndex(prev => 
        prev > 0 ? prev - 1 : filteredStudiesForDropdown.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selectedStudy = filteredStudiesForDropdown[studyDropdownIndex];
      if (selectedStudy) {
        addStudy(selectedStudy);
      }
    } else if (e.key === 'Escape') {
      setShowStudyDropdown(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        study_ids: selectedStudies.map(s => s.id),
      };
      
      if (editingProfile) {
        await profilesApi.update(editingProfile.id, dataToSend);
        toast.success("Perfil actualizado");
      } else {
        await profilesApi.create(dataToSend);
        toast.success("Perfil creado");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al guardar perfil");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Está seguro de eliminar este perfil?")) return;
    try {
      await profilesApi.delete(id);
      toast.success("Perfil eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const filteredProfiles = profiles.filter(
    (profile) =>
      profile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get price list name
  const getPriceListName = (priceListId) => {
    const pl = priceLists.find(p => p.id === priceListId);
    return pl?.name || "-";
  };

  return (
    <div className="space-y-6" data-testid="profiles-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
            Perfiles
          </h1>
          <p className="text-muted-foreground">
            Administre los perfiles de estudios
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-profile-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Perfil
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Layers className="h-5 w-5" />
              Catálogo de Perfiles
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Estudios</TableHead>
                  <TableHead>Precio</TableHead>
                  <TableHead>Lista de Precios</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProfiles.map((profile) => (
                  <TableRow key={profile.id}>
                    <TableCell className="font-mono">{profile.code}</TableCell>
                    <TableCell className="font-medium">{profile.name}</TableCell>
                    <TableCell>{profile.study_ids?.length || 0}</TableCell>
                    <TableCell>${profile.price.toLocaleString()}</TableCell>
                    <TableCell>{getPriceListName(profile.price_list_id)}</TableCell>
                    <TableCell>
                      <Badge variant={profile.active ? "default" : "secondary"}>
                        {profile.active ? "Activo" : "Inactivo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(profile)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(profile.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProfile ? "Editar Perfil" : "Nuevo Perfil"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, code: e.target.value }))
                  }
                  required
                  data-testid="profile-code"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                  data-testid="profile-name"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Precio *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      price: parseFloat(e.target.value) || 0,
                    }))
                  }
                  required
                  data-testid="profile-price"
                />
              </div>
              <div className="space-y-2">
                <Label>Lista de Precios</Label>
                <Select
                  value={formData.price_list_id}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, price_list_id: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar lista..." />
                  </SelectTrigger>
                  <SelectContent>
                    {priceLists.map((pl) => (
                      <SelectItem key={pl.id} value={pl.id}>
                        {pl.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Study search */}
            <div className="space-y-2">
              <Label>Estudios incluidos</Label>
              <div className="relative" ref={studySearchRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar estudio por nombre o código..."
                  value={studySearchTerm}
                  onChange={(e) => {
                    setStudySearchTerm(e.target.value);
                    setShowStudyDropdown(e.target.value.length > 0);
                    setStudyDropdownIndex(0);
                  }}
                  onFocus={() => {
                    if (studySearchTerm) {
                      setShowStudyDropdown(true);
                      setStudyDropdownIndex(0);
                    }
                  }}
                  onKeyDown={handleStudySearchKeyDown}
                  className="pl-10"
                />
                {studySearchTerm && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setStudySearchTerm("");
                      setShowStudyDropdown(false);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                
                {/* Dropdown */}
                {showStudyDropdown && filteredStudiesForDropdown.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredStudiesForDropdown.map((study, index) => (
                      <button
                        key={study.id}
                        type="button"
                        className={`w-full px-4 py-2 text-left flex justify-between items-center transition-colors ${
                          index === studyDropdownIndex
                            ? "bg-primary/20 border-l-4 border-l-primary"
                            : "hover:bg-muted"
                        }`}
                        onClick={() => addStudy(study)}
                        onMouseEnter={() => setStudyDropdownIndex(index)}
                      >
                        <span>
                          {study.code && (
                            <span className="font-mono text-muted-foreground mr-2">
                              {study.code}
                            </span>
                          )}
                          {study.name}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                
                {showStudyDropdown && studySearchTerm && filteredStudiesForDropdown.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg p-4 text-center text-muted-foreground">
                    No se encontraron estudios
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Usa ↑↓ para navegar y Enter para agregar
              </p>
            </div>
            
            {/* Selected studies list */}
            <div className="border rounded-lg max-h-48 overflow-y-auto">
              {selectedStudies.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  No hay estudios seleccionados
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead className="w-20">Orden</TableHead>
                      <TableHead>Código</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedStudies.map((study, index) => (
                      <TableRow key={study.id}>
                        <TableCell className="font-mono text-muted-foreground">
                          {index + 1}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => moveStudyUp(index)}
                              disabled={index === 0}
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => moveStudyDown(index)}
                              disabled={index === selectedStudies.length - 1}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{study.code}</TableCell>
                        <TableCell>{study.name}</TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            onClick={() => removeStudy(study.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-profile-btn">
                {editingProfile ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
